import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { Field } from "@/components/Field";
import { friendlyError, sherpApi, type SherpForm, type SherpPrefill } from "@/lib/api";

type Props = { onDone: () => void };

type Stage =
  | { kind: "loading" }
  | { kind: "rules"; html: string }
  | { kind: "form" }
  | { kind: "preview"; html: string }
  | { kind: "otp" }
  | { kind: "signed" }
  | { kind: "view"; html: string }
  | { kind: "error"; message: string };

const EMPTY_FORM: SherpForm = {
  inn: "",
  snils: "",
  place_of_birth: "",
  citizenship: "Российская Федерация",
  actual_address: "",
  is_pdl: false,
  pdl_details: "",
  fatca_us: false,
  fatca_tin: "",
  crs_details: "",
  source_of_funds: "salary",
  source_other: "",
  agree_all: false,
};

export function Sherp({ onDone }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [prefill, setPrefill] = useState<SherpPrefill | null>(null);
  const [form, setForm] = useState<SherpForm>(EMPTY_FORM);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [inlineErr, setInlineError] = useState<string | null>(null);
  const started = useRef(false);

  const boot = useCallback(async () => {
    try {
      const st = await sherpApi.status();
      if (!st.enabled) {
        onDone();
        return;
      }
      if (st.signed) {
        setStage({ kind: "signed" });
        return;
      }
      const pf = await sherpApi.prefill();
      setPrefill(pf);
      if (pf.form) setForm({ ...EMPTY_FORM, ...pf.form });
      const r = await sherpApi.rules();
      setStage({ kind: "rules", html: r.html });
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось загрузить документы. Попробуйте позже.") });
    }
  }, [onDone]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void boot();
  }, [boot]);

  const set = <K extends keyof SherpForm>(k: K, v: SherpForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submitForm = useCallback(async () => {
    if (busy) return;
    setInlineError(null);
    if (!form.inn || !form.snils || !form.place_of_birth) {
      setInlineError("Заполните ИНН, СНИЛС и место рождения.");
      return;
    }
    if (!form.agree_all) {
      setInlineError("Подтвердите согласия, отметив галочку.");
      return;
    }
    setBusy(true);
    try {
      const doc = await sherpApi.saveForm(form);
      if (!doc.html) throw new Error("empty document");
      setStage({ kind: "preview", html: doc.html });
    } catch (e) {
      setInlineError(friendlyError(e, "Не удалось сформировать заявление."));
    } finally {
      setBusy(false);
    }
  }, [busy, form]);

  const requestOtp = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await sherpApi.sendOtp();
      setOtp("");
      setInlineError(null);
      setStage({ kind: "otp" });
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось отправить код. Попробуйте ещё раз.") });
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const viewDocument = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const doc = await sherpApi.document();
      if (!doc.html) throw new Error("empty document");
      setStage({ kind: "view", html: doc.html });
    } catch (e) {
      setInlineError(friendlyError(e, "Не удалось загрузить документ."));
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const confirm = useCallback(async () => {
    const code = otp.trim();
    if (code.length < 4 || busy) return;
    setBusy(true);
    try {
      const r = await sherpApi.confirm(code);
      if (r.signed) setStage({ kind: "signed" });
      else setStage({ kind: "error", message: "Подпись не подтверждена. Попробуйте ещё раз." });
    } catch (e) {
      setOtp("");
      setInlineError(friendlyError(e, "Неверный код. Проверьте письмо и попробуйте снова."));
    } finally {
      setBusy(false);
    }
  }, [otp, busy]);

  useBackButton(
    stage.kind === "signed" ? undefined : stage.kind === "view" ? () => setStage({ kind: "signed" }) : onDone,
  );

  const mainBtn = (() => {
    if (stage.kind === "rules")
      return { text: "Ознакомлен, продолжить", onClick: () => setStage({ kind: "form" }), active: true, visible: true, loading: false };
    if (stage.kind === "form")
      return { text: "Сформировать заявление", onClick: submitForm, active: !busy && form.agree_all, visible: true, loading: busy };
    if (stage.kind === "preview")
      return { text: "Подписать по email", onClick: requestOtp, active: !busy, visible: true, loading: busy };
    if (stage.kind === "otp")
      return { text: "Подтвердить", onClick: confirm, active: otp.trim().length >= 4 && !busy, visible: true, loading: busy };
    if (stage.kind === "signed") return { text: "Готово", onClick: onDone, active: true, visible: true, loading: false };
    if (stage.kind === "view")
      return { text: "Готово", onClick: onDone, active: true, visible: true, loading: false };
    return { text: "", onClick: () => {}, active: false, visible: false, loading: false };
  })();
  useMainButton(mainBtn);

  const title =
    stage.kind === "signed"
      ? "Заявление подписано ✓"
      : stage.kind === "view"
      ? "Подписанное Заявление"
      : stage.kind === "otp"
      ? "Подтверждение по email"
      : stage.kind === "preview"
      ? "Проверьте заявление"
      : stage.kind === "form"
      ? "Заявление о присоединении"
      : stage.kind === "error"
      ? "Не получилось"
      : "Правила оказания услуг ЗАО «ШЭРП»";

  const subtitle =
    stage.kind === "rules"
      ? "Ознакомьтесь с Правилами приёма платежей в пользу Брокера через платёжного оператора."
      : stage.kind === "form"
      ? "Дозаполните недостающие сведения и подтвердите согласия."
      : stage.kind === "preview"
      ? "Это итоговый документ. Для подписания отправим код на ваш email."
      : undefined;

  return (
    <Screen title={title} subtitle={subtitle}>
      {stage.kind === "loading" && (
        <div className="flex flex-col items-center gap-3 py-16 text-white/45">
          <Spinner className="h-7 w-7" />
          <div className="text-[14px]">Загрузка…</div>
        </div>
      )}

      {stage.kind === "rules" && (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white">
          <iframe title="Правила ЗАО «ШЭРП»" srcDoc={stage.html} sandbox="" className="h-[60vh] w-full" />
        </div>
      )}

      {stage.kind === "form" && prefill && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3 text-[13px] text-white/45">
            <div className="font-medium text-white">{prefill.client.full_name || "—"}</div>
            <div>{prefill.client.passport || "паспортные данные не заполнены"}</div>
          </div>

          <Field label="ИНН" inputMode="numeric" value={form.inn} onChange={(e) => set("inn", e.target.value)} placeholder="12 цифр" />
          <Field label="СНИЛС" value={form.snils} onChange={(e) => set("snils", e.target.value)} placeholder="000-000-000 00" />
          <Field label="Место рождения" value={form.place_of_birth} onChange={(e) => set("place_of_birth", e.target.value)} placeholder="страна, регион, город" />
          <Field label="Гражданство" value={form.citizenship} onChange={(e) => set("citizenship", e.target.value)} />
          <Field
            label="Адрес фактического проживания (если отличается)"
            value={form.actual_address}
            onChange={(e) => set("actual_address", e.target.value)}
            placeholder="можно не заполнять"
          />

          <label className="block">
            <span className="mb-1.5 block text-[13px] text-white/45">Основной источник средств</span>
            <select
              value={form.source_of_funds}
              onChange={(e) => set("source_of_funds", e.target.value)}
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3.5 text-[16px] text-white outline-none [color-scheme:dark]"
            >
              {prefill.source_of_funds_options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {form.source_of_funds === "other" && (
            <Field label="Уточните источник средств" value={form.source_other} onChange={(e) => set("source_other", e.target.value)} />
          )}

          <label className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3">
            <input type="checkbox" checked={form.is_pdl} onChange={(e) => set("is_pdl", e.target.checked)} className="mt-1" />
            <span className="text-[14px]">Я являюсь политически значимым лицом (ПДЛ), членом семьи или связанным лицом ПДЛ</span>
          </label>
          {form.is_pdl && (
            <Field label="Должность, орган, страна" value={form.pdl_details} onChange={(e) => set("pdl_details", e.target.value)} />
          )}

          <label className="flex items-start gap-3 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3">
            <input type="checkbox" checked={form.fatca_us} onChange={(e) => set("fatca_us", e.target.checked)} className="mt-1" />
            <span className="text-[14px]">Я являюсь налоговым резидентом США (FATCA)</span>
          </label>
          {form.fatca_us && <Field label="TIN (США)" value={form.fatca_tin} onChange={(e) => set("fatca_tin", e.target.value)} />}

          <Field
            label="Иные страны налогового резидентства (CRS) с TIN/ИНН"
            value={form.crs_details}
            onChange={(e) => set("crs_details", e.target.value)}
            placeholder="можно не заполнять"
          />

          <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={form.agree_all} onChange={(e) => set("agree_all", e.target.checked)} className="mt-1" />
              <span className="text-[14px]">
                Подтверждаю ознакомление с Правилами и даю все необходимые согласия (обработка и передача ПДн,
                санкционный скрининг и пр.), а также подтверждаю, что действую за свой счёт.
              </span>
            </label>
            <ul className="mt-2 list-disc space-y-1 pl-8 text-[11px] text-white/45">
              {prefill.consents.slice(2).map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>

          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
        </div>
      )}

      {stage.kind === "preview" && (
        <div className="space-y-3">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white">
            <iframe title="Заявление о присоединении" srcDoc={stage.html} sandbox="" className="h-[60vh] w-full" />
          </div>
          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
        </div>
      )}

      {stage.kind === "otp" && (
        <div className="space-y-3">
          <Note>Мы отправили код на ваш email. Введите его для подписания Заявления.</Note>
          <input
            inputMode="numeric"
            autoFocus
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 8));
              setInlineError(null);
            }}
            placeholder="Код из письма"
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3.5 text-center text-[20px] tracking-[0.3em] text-white outline-none placeholder:text-white/30 focus:border-white/20 [color-scheme:dark]"
          />
          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
          <button onClick={requestOtp} disabled={busy} className="w-full py-2 text-center text-[13px] text-brand active:opacity-70">
            Отправить код повторно
          </button>
        </div>
      )}

      {stage.kind === "signed" && (
        <div className="space-y-3">
          <div className="rounded-2xl bg-emerald-500/10 p-5 text-center">
            <div className="text-3xl">✅</div>
            <div className="mt-2 text-[15px]">Заявление о присоединении подписано.</div>
          </div>
          <button
            onClick={viewDocument}
            disabled={busy}
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-[15px] active:opacity-70 disabled:opacity-50"
          >
            {busy ? "Загрузка…" : "📄 Посмотреть документ"}
          </button>
          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
        </div>
      )}

      {stage.kind === "view" && (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white">
          <iframe
            title="Подписанное Заявление"
            srcDoc={stage.html}
            sandbox=""
            className="h-[68vh] w-full"
          />
        </div>
      )}

      {stage.kind === "error" && (
        <div className="space-y-3">
          <Note variant="danger">{stage.message}</Note>
          <Button
            onClick={() => {
              started.current = false;
              setStage({ kind: "loading" });
              void boot();
            }}
          >
            Попробовать снова
          </Button>
        </div>
      )}

      {stage.kind === "rules" && <FallbackButton text="Ознакомлен, продолжить" onClick={() => setStage({ kind: "form" })} />}
      {stage.kind === "form" && (
        <FallbackButton text="Сформировать заявление" onClick={submitForm} disabled={!form.agree_all} loading={busy} />
      )}
      {stage.kind === "preview" && <FallbackButton text="Подписать по email" onClick={requestOtp} loading={busy} />}
      {stage.kind === "otp" && (
        <FallbackButton text="Подтвердить" onClick={confirm} disabled={otp.trim().length < 4} loading={busy} />
      )}
      {stage.kind === "signed" && <FallbackButton text="Готово" onClick={onDone} />}
      {stage.kind === "view" && <FallbackButton text="Готово" onClick={onDone} />}
    </Screen>
  );
}
